import { useDebounce } from 'use-debounce';

import { urls } from 'src/urls/dashboard';
import { escapeBbqlString } from 'src/utils/escape-bbql-string';
import { useQuery } from 'src/utils/fb-query';
import { authRequest } from 'src/utils/fetch';
import { captureExceptionWithTags } from 'src/utils/sentry';

import type { RecentRepository } from './types';

export const RECENT_REPOS_DISPLAY_LIMIT = 3;
export const QUERY_DEBOUNCE_MS = 250;

/**
 * Maps a raw BB.Repository from the 2.0 API to the leaner
 * RecentRepository shape consumed by the search dialog.
 */
function toRecentRepository(repo: BB.Repository): RecentRepository {
  return {
    id: repo.uuid,
    name: repo.name,
    slug: repo.slug ?? repo.full_name.split('/')[1] ?? '',
    href: repo.links.html.href,
    avatarHref: repo.links.avatar.href,
    workspace: repo.full_name.split('/')[0],
    updatedOn: repo.updated_on,
  };
}

export type UseRecentlyUpdatedRepositoriesResult = {
  repositories: RecentRepository[];
  isLoading: boolean;
};

type FetchArgs = {
  workspaceSlug: string;
  query: string;
  pageLength?: number;
};

/**
 * Builds the API URL for fetching repositories, optionally filtered by query.
 */
export function buildRepositoriesUrl(
  workspaceSlug: string,
  query: string,
  pageLength: number = RECENT_REPOS_DISPLAY_LIMIT
): string {
  const params: Record<string, string | number> = {
    pagelen: pageLength,
    sort: '-updated_on',
  };

  if (query) {
    const escaped = escapeBbqlString(query);
    params.q = `name ~ "${escaped}" OR slug ~ "${escaped}"`;
  }

  return urls.api.v20.profileRepositories(workspaceSlug, params);
}

/**
 * Fetches repositories for the given workspace and optional query filter.
 */
async function fetchRepositories({
  workspaceSlug,
  query,
  pageLength,
}: FetchArgs): Promise<RecentRepository[]> {
  const url = buildRepositoriesUrl(workspaceSlug, query, pageLength);
  const response = await fetch(authRequest(url));

  if (!response.ok) {
    throw new Error(
      `Failed to fetch repositories: ${response.status} ${response.statusText}`
    );
  }

  const data: { values: BB.Repository[] } = await response.json();
  return data.values.map(toRecentRepository);
}

/**
 * Fetches repositories for a workspace and returns them as `RecentRepository[]`.
 *
 * When `query` is empty, returns the most recently updated repositories.
 * When `query` is provided, filters repositories by name/slug match.
 * Query changes are debounced to avoid firing on every keystroke.
 */
export function useRecentlyUpdatedRepositories(
  workspaceSlug: string,
  isExpanded: boolean,
  query = '',
  pageLength?: number
): UseRecentlyUpdatedRepositoriesResult {
  // Debounce query changes to avoid firing on every keystroke.
  const [debouncedQuery] = useDebounce(query, QUERY_DEBOUNCE_MS);

  const queryResult = useQuery(
    fetchRepositories,
    {
      workspaceSlug,
      query: debouncedQuery,
      pageLength,
    },
    {
      enabled: !!workspaceSlug && isExpanded,
      onError: error => {
        captureExceptionWithTags(error, {
          component: 'bitbucket-search-recent-repositories',
        });
      },
    }
  );

  return {
    repositories: queryResult.data ?? [],
    isLoading: queryResult.isPending,
  };
}
